'use client';

import { useRef, useState, useEffect, type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import type { Components } from 'react-markdown';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

// ---------------------------------------------------------------------------
// Responsive table wrapper — counts columns via DOM after mount
// ---------------------------------------------------------------------------

function ResponsiveTableWrapper({ children, ...props }: { children: ReactNode; [key: string]: unknown }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isWide, setIsWide] = useState(false);

  useEffect(() => {
    if (containerRef.current) {
      const table = containerRef.current.querySelector('table');
      if (table) {
        const firstRow = table.querySelector('tr');
        if (firstRow) {
          const cellCount = firstRow.querySelectorAll('th, td').length;
          if (cellCount >= 3) {
            setIsWide(true);
          }
        }
      }
    }
  }, []);

  return (
    <div
      ref={containerRef}
      className="mb-3 rounded-lg border border-border w-full briefing-table-scroll-container relative"
    >
      <div className="overflow-x-auto">
        <table className={cn('w-full table-auto text-xs', isWide && 'min-w-max')} {...props}>
          {children}
        </table>
      </div>
      {/* Fade shadow hint for scrollable content on mobile/tablet */}
      <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-background/80 to-transparent rounded-r-lg lg:hidden" aria-hidden="true" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Priority badge detection and rendering
// ---------------------------------------------------------------------------

const PRIORITY_PATTERNS: {
  pattern: RegExp;
  variant: 'destructive' | 'warning' | 'secondary' | 'success';
}[] = [
  { pattern: /PFLICHT/i, variant: 'destructive' },
  { pattern: /KRITISCH/i, variant: 'destructive' },
  { pattern: /MITTEL[\s-]*HOCH/i, variant: 'warning' },
  { pattern: /HOCH/i, variant: 'destructive' },
  { pattern: /MITTEL/i, variant: 'warning' },
  { pattern: /NIEDRIG/i, variant: 'secondary' },
  { pattern: /GERING/i, variant: 'secondary' },
  { pattern: /OPTIONAL/i, variant: 'secondary' },
];

function detectPriorityBadge(
  text: string
): { variant: 'destructive' | 'warning' | 'secondary' | 'success'; label: string } | null {
  const trimmed = text.trim();
  for (const { pattern, variant } of PRIORITY_PATTERNS) {
    if (pattern.test(trimmed)) {
      return { variant, label: trimmed };
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Custom react-markdown components
// ---------------------------------------------------------------------------

const markdownComponents: Components = {
  // Headings
  h1: ({ children, ...props }) => (
    <h1 className="text-2xl font-bold text-foreground mt-6 mb-3 first:mt-0" {...props}>
      {children}
    </h1>
  ),
  h2: ({ children, ...props }) => (
    <h2 className="text-xl font-bold text-foreground mt-6 mb-3 first:mt-0" {...props}>
      {children}
    </h2>
  ),
  h3: ({ children, ...props }) => (
    <h3 className="text-lg font-semibold text-foreground mt-5 mb-2 first:mt-0" {...props}>
      {children}
    </h3>
  ),
  h4: ({ children, ...props }) => (
    <h4 className="text-base font-semibold text-foreground mt-4 mb-2 first:mt-0" {...props}>
      {children}
    </h4>
  ),

  // Paragraphs
  p: ({ children, ...props }) => (
    <p className="text-sm leading-relaxed text-foreground mb-3 last:mb-0" {...props}>
      {children}
    </p>
  ),

  // Strong/Bold
  strong: ({ children, ...props }) => (
    <strong className="font-semibold text-foreground break-words" {...props}>
      {children}
    </strong>
  ),

  // Emphasis
  em: ({ children, ...props }) => (
    <em className="italic" {...props}>
      {children}
    </em>
  ),

  // Links
  a: ({ children, href, ...props }) => (
    <a
      href={href}
      className="text-accent underline underline-offset-2 hover:text-accent/80"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    >
      {children}
    </a>
  ),

  // Lists
  ul: ({ children, ...props }) => (
    <ul className="list-disc list-outside ml-5 mb-3 space-y-1 text-sm text-foreground" {...props}>
      {children}
    </ul>
  ),
  ol: ({ children, ...props }) => (
    <ol className="list-decimal list-outside ml-5 mb-3 space-y-1 text-sm text-foreground" {...props}>
      {children}
    </ol>
  ),
  li: ({ children, ...props }) => (
    <li className="leading-relaxed" {...props}>
      {children}
    </li>
  ),

  // Blockquotes
  blockquote: ({ children, ...props }) => (
    <blockquote
      className="border-l-4 border-accent/40 pl-4 italic text-muted-foreground my-3"
      {...props}
    >
      {children}
    </blockquote>
  ),

  // Line breaks
  br: () => <br />,

  // Horizontal rule
  hr: (props) => <hr className="my-4 border-border" {...props} />,

  // Code
  code: ({ children, className, ...props }) => {
    const isInline = !className;
    if (isInline) {
      return (
        <code
          className="rounded bg-muted px-1.5 py-0.5 text-xs font-mono text-foreground"
          {...props}
        >
          {children}
        </code>
      );
    }
    return (
      <code className={cn('text-xs font-mono', className)} {...props}>
        {children}
      </code>
    );
  },
  pre: ({ children, ...props }) => (
    <pre
      className="rounded-lg bg-muted p-4 overflow-x-auto text-xs mb-3"
      {...props}
    >
      {children}
    </pre>
  ),

  // Tables — smart layout: ≤3 columns auto layout, ≥4 columns horizontal scroll
  table: ({ children, ...props }) => (
    <ResponsiveTableWrapper {...props}>{children}</ResponsiveTableWrapper>
  ),
  thead: ({ children, ...props }) => (
    <thead className="bg-muted/50" {...props}>
      {children}
    </thead>
  ),
  tbody: ({ children, ...props }) => (
    <tbody className="divide-y divide-border" {...props}>
      {children}
    </tbody>
  ),
  tr: ({ children, ...props }) => (
    <tr className="border-border" {...props}>
      {children}
    </tr>
  ),
  th: ({ children, ...props }) => (
    <th
      className="px-2 py-1.5 sm:px-3 sm:py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider [word-break:break-word]"
      {...props}
    >
      {children}
    </th>
  ),
  td: ({ children, ...props }) => {
    // Helper: render badge + optional description on its own line
    function renderBadgeCell(rawText: string, tdProps: object) {
      const sepIdx = rawText.indexOf(' — ');
      const priorityPart = sepIdx >= 0 ? rawText.slice(0, sepIdx).trim() : rawText.trim();
      const description = sepIdx >= 0 ? rawText.slice(sepIdx + 3).trim() : '';
      const badge = detectPriorityBadge(priorityPart);
      if (!badge) return null;
      return (
        <td className="px-2 py-1.5 sm:px-3 sm:py-2 text-xs" {...tdProps}>
          <Badge variant={badge.variant} className="text-[10px]">
            {priorityPart}
          </Badge>
          {description && (
            <span className="block mt-0.5 text-foreground">{description}</span>
          )}
        </td>
      );
    }

    // Check if cell content is a plain string with a priority badge
    const text = typeof children === 'string' ? children : '';
    if (text) {
      const cell = renderBadgeCell(text, props);
      if (cell) return cell;
    }

    // Check children array for a leading string or React element that might be a badge
    if (Array.isArray(children)) {
      // Plain string child (e.g. "Hoch — description")
      const firstStringChild = children.find((c) => typeof c === 'string' && c.trim());
      if (typeof firstStringChild === 'string') {
        const cell = renderBadgeCell(firstStringChild, props);
        if (cell) return cell;
      }

      // React element first child, e.g. <strong> from "**PFLICHT 24 MONATE:** description..."
      const first = children[0];
      if (first !== null && first !== undefined && typeof first === 'object' && !Array.isArray(first)) {
        const el = first as { props?: { children?: unknown } };
        const innerText = typeof el.props?.children === 'string' ? el.props.children : '';
        if (innerText) {
          const badge = detectPriorityBadge(innerText);
          if (badge) {
            const description = children
              .slice(1)
              .map((c) => (typeof c === 'string' ? c : ''))
              .join('')
              .trim();
            const label = innerText.replace(/:$/, '').trim();
            return (
              <td className="px-2 py-1.5 sm:px-3 sm:py-2 text-xs" {...props}>
                <Badge variant={badge.variant} className="text-[10px]">
                  {label}
                </Badge>
                {description && (
                  <span className="block mt-0.5 text-foreground">{description}</span>
                )}
              </td>
            );
          }
        }
      }
    }

    return (
      <td className="px-2 py-1.5 sm:px-3 sm:py-2 text-xs text-foreground [overflow-wrap:anywhere] whitespace-normal" {...props}>
        {children}
      </td>
    );
  },

  // Images
  img: ({ alt, src, ...props }) => (
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={src}
      alt={alt ?? ''}
      className="max-w-full h-auto rounded-lg my-3"
      loading="lazy"
      {...props}
    />
  ),
};

// ---------------------------------------------------------------------------
// BriefingContent component
// ---------------------------------------------------------------------------

interface BriefingContentProps {
  markdown: string;
  className?: string;
}

export function BriefingContent({ markdown, className }: BriefingContentProps) {
  return (
    <div className={cn('briefing-content w-full min-w-0', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeSanitize]}
        components={markdownComponents}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
}
